/**
 * Client management hook
 * 
 * Manages matchmaking clients and selected client state.
 * Persists selected client ID in localStorage to maintain selection
 * across tab changes and page reloads.
 *
 * NOTE (Feature 054): This hook uses the matchmaking-local `useClients`
 * from `useMatchmaking.ts` (calls stored procedure `/professionals/matchmaking/clients`)
 * — NOT the unified `useClients` from `@/features/client-relationships`
 * (which calls `GET /api/v1/clients`). The matchmaking stored procedures
 * return additional fields (ageRange, relative, zipCode) needed for
 * matchmaking context that the unified endpoint does not provide.
 */

import { useState, useEffect, useMemo, useCallback } from 'react';
import { useAuth } from '@/features/authentication/hooks/useAuth';
import { useClients, useCurrentUserProfile } from '../hooks/useMatchmaking';
import { mapApiClientToDashboardClient, type DashboardClient, type AdvisorClientApi } from '../utils/client.utils';

/**
 * Storage key for persisting selected client ID
 */
const SELECTED_CLIENT_STORAGE_KEY = 'matchmaking_selected_client_id';

/**
 * Get persisted selected client ID from localStorage
 */
const getPersistedClientId = (): string | null => {
  try {
    const stored = localStorage.getItem(SELECTED_CLIENT_STORAGE_KEY);
    return stored || null;
  } catch {
    return null;
  }
};

/**
 * Persist selected client ID to localStorage
 */
const persistClientId = (clientId: string | null): void => {
  try {
    if (clientId) {
      localStorage.setItem(SELECTED_CLIENT_STORAGE_KEY, clientId);
    } else {
      localStorage.removeItem(SELECTED_CLIENT_STORAGE_KEY);
    }
  } catch {
    // Ignore localStorage errors (e.g., in private browsing)
  }
};

export const useClientManagement = () => {
  const { user: loggedInUser } = useAuth();
  const [clients, setClients] = useState<DashboardClient[]>([]);
  const [clientsError, setClientsError] = useState<string | null>(null);
  // Initialize with persisted client ID if available
  const [selectedClientId, setSelectedClientIdState] = useState<string | null>(() => 
    getPersistedClientId()
  );
  const [advisorProfileId, setAdvisorProfileId] = useState<string | null>(null);

  /**
   * Wrapper for setSelectedClientId that also persists to localStorage
   */
  const setSelectedClientId = useCallback((clientId: string | null) => {
    setSelectedClientIdState(clientId);
    persistClientId(clientId);
  }, []);

  // Fetch advisor profile to get the advisor ID
  const { data: advisorProfile } = useCurrentUserProfile();
  
  useEffect(() => {
    if (advisorProfile?.id) {
      setAdvisorProfileId(advisorProfile.id);
    } else if (loggedInUser?.id) {
      // Fallback to loggedInUser.id if profile fetch fails
      setAdvisorProfileId(loggedInUser.id);
    } else {
      setAdvisorProfileId(null);
    }
  }, [advisorProfile?.id, loggedInUser?.id]);

  // Fetch clients using React Query
  // Feature 036: Pass user role for role-based client routing
  const { data: clientsData, isLoading: clientsLoading, isError: clientsIsError, error: clientsErrorData } = useClients(loggedInUser?.role);

  useEffect(() => {
    if (!loggedInUser?.id) {
      setClients([]);
      setSelectedClientId(null);
      setClientsError(null);
      return;
    }

    if (clientsData) {
      // New API returns { status, message, clients } format
      const clientsArray: AdvisorClientApi[] = 
        clientsData.clients && Array.isArray(clientsData.clients)
          ? clientsData.clients
          : Array.isArray(clientsData)
            ? clientsData
            : [];
      const mappedClients = clientsArray.map(mapApiClientToDashboardClient);
      setClients(mappedClients);
      
      // Always prioritize persisted ID from localStorage (most reliable for tab switching)
      // This ensures that when user switches tabs and comes back, the persisted client is restored
      const persistedId = getPersistedClientId();
      let clientIdToSet: string | null = null;
      
      // First priority: Persisted ID from localStorage (always check this first)
      if (persistedId && mappedClients.some((client) => client.id === persistedId)) {
        clientIdToSet = persistedId;
      } 
      // Second priority: Current state (if persisted ID is invalid or doesn't exist)
      else if (selectedClientId && mappedClients.some((client) => client.id === selectedClientId)) {
        clientIdToSet = selectedClientId;
      }
      // Third priority: Fallback to first client (only if no valid persisted or current ID)
      else if (mappedClients.length > 0) {
        clientIdToSet = mappedClients[0].id;
      }
      
      // Update state and persist if we have a client ID and it's different from current
      // This prevents unnecessary updates but ensures persisted ID is always restored
      if (clientIdToSet && clientIdToSet !== selectedClientId) {
        setSelectedClientIdState(clientIdToSet);
        persistClientId(clientIdToSet);
      } else if (clientIdToSet === selectedClientId) {
        // Ensure it's persisted even if state matches (handles edge cases)
        persistClientId(clientIdToSet);
      }
      
      setClientsError(null);
    } else if (clientsIsError) {
      setClients([]);
      setClientsError(
        clientsErrorData instanceof Error
          ? clientsErrorData.message
          : 'Failed to fetch advisor clients',
      );
      // Don't clear persisted ID on error - keep it for when clients load successfully
      // setSelectedClientId(null);
    }
    // Note: selectedClientId is intentionally not in deps to avoid loops
    // We check it directly in the effect body
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientsData, clientsIsError, clientsErrorData, loggedInUser?.id]);

  const selectedClient = useMemo(() => {
    if (selectedClientId) {
      const found = clients.find((client) => client.id === selectedClientId);
      if (found) return found;
      // If selectedClientId doesn't match any client (stale ID), fall back to first client
      // and update the state to fix the mismatch
      if (clients.length > 0) {
        // Schedule state update for next tick to avoid updating during render
        setTimeout(() => {
          setSelectedClientId(clients[0].id);
        }, 0);
        return clients[0];
      }
      return null;
    }
    return clients[0] ?? null;
  }, [clients, selectedClientId, setSelectedClientId]);

  return {
    clients,
    clientsError,
    clientsLoading,
    selectedClientId,
    setSelectedClientId,
    advisorProfileId,
    selectedClient,
    hasClients: clients.length > 0,
  };
};

